import React, { useState, useEffect, useContext } from "react";
import Modal from "../../../components/Modal";
import TextFieldWithLabel from "../../../components/TextFieldWithLabel";
import { useFetchApi } from "../../../hooks/useFetchApi";
import Dropdown from "../../../components/NewDropdown";
import { DispatchContext, StateContext } from "../../../store/context";
import Caption from "../../../components/Typography/Caption";
import { DatePicker } from "antd";
import { add, format, parseISO } from "date-fns";
import { STATUS_CODES } from "http";
import API from "../../../network";
import Alert from "../../../components/Alert";

const AddNewModal: React.FC<{
  status: boolean;
  onClose: any;
  onSuccess?: any;
  data?: any;
  stockMarkets?: any;
  roles?: any;
}> = ({
  status,
  onClose,
  onSuccess,
  data = {},
  stockMarkets = [],
  roles = [],
}): React.ReactElement => {
  const { authUser = {} } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const [d, setD] = useState<any>(data || {});
  const [error, setError] = useState("");
  const [emailOnly, setEmailOnly] = useState(false);
  const {
    execute,
    loading: registerLoading,
    response: newUser,
  } = useFetchApi(
    "/api/register",
    {},
    data && Object.keys(data).length ? "PUT" : "POST",
    onSuccess
  );

  const {
    execute: executeBulkUsers,
    loading: bulkUsersLoading,
    response: bulkUsersResponse,
  } = useFetchApi("/api/users/bulk", {}, "POST", onSuccess);

  const {
    loading: subscriptionsLoading,
    execute: getSubscriptions,
    response: subscriptions,
  } = useFetchApi("/api/subscriptions", {}, "GET");

  const handleChange = (e: any) => {
    const id = e.target.id;
    let value;
  
    if (id === "generate_password") {
      value = e.target.checked;
    } else if (id === "status") {
      value = e.target.checked ? 1 : 0;
    } else {
      value = e.target.value;
    }

    setD({ ...d, [id]: value });
  };

  const handleEmailOnlyChange = (e: any) => {
    const checked = e.target.checked;
    setEmailOnly(checked);
    if (checked) {
      setD({ email: d.email });
    }
  };

  const handleOnSubmit = (e: any) => {
    if (emailOnly) {
      // For email-only, send to bulk users endpoint
      if (!d.email) {
        setError("Email is required");
        dispatch({
          type: "UPDATE_ALERT",
          payload: { type: "error", message: "Email is required" },
        });
        return;
      }

      const emailArray = [d.email];

      executeBulkUsers({
        users: emailArray,
        auth: authUser?.token ? authUser.token : undefined,
      });
    } else {
      execute({
        ...d,
        status: d.status === true || d.status === 1 ? 1 : 0,
        id: data.id ? data.id : undefined,
        auth: authUser?.token ? authUser.token : undefined,
      });
    }
    const _e = { ...e, target: { ...e.target, id: "newEdit" } };
  };

  const handleClose = (e: any) => {
    const _e = { ...e, target: { ...e.target, id: "newEdit" } };
    onClose(_e);
  };

  useEffect(() => {
    if (data && Object.keys(data).length) {
      setD({ ...data, generate_password: false });
    }

    return () => {
      setD({});
    };
  }, [data]);

  useEffect(() => {
    getSubscriptions();
  }, []);

  useEffect(() => {
    if (newUser && newUser.status === "error") {
      setError(newUser.message);
      dispatch({
        type: "UPDATE_ALERT",
        payload: { type: "error", message: newUser.message },
      });
    } else if (newUser && newUser.status === "success") {
      onSuccess();
      onClose({ target: { id: "newEdit" } });
    }
  }, [newUser]);

  useEffect(() => {
    if (bulkUsersResponse && bulkUsersResponse.data) {
      if (bulkUsersResponse.status === "error") {
        setError(bulkUsersResponse.message);
        dispatch({
          type: "UPDATE_ALERT",
          payload: { type: "error", message: bulkUsersResponse.message },
        });
      } else if (bulkUsersResponse.status === "success") {
        onSuccess();
        onClose({ target: { id: "newEdit" } });
        // dispatch({
        //   type: "UPDATE_ALERT",
        //   payload: { type: "success", message: bulkUsersResponse.message },
        // });
      }
    }
    setEmailOnly(false);
  }, [bulkUsersResponse]);

  return (
    <Modal
      heading="Add/Edit User"
      status={status}
      onClose={handleClose}
      onSubmit={handleOnSubmit}
    >
      <>
        <div className="flex w-full">
          <Alert />
        </div>
        <div className="flex w-full flex-col pb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={emailOnly}
              onChange={handleEmailOnlyChange}
              className="w-4 h-4"
            />
            <Caption className="mb-0">With Email Only</Caption>
          </label>
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:gap-4 mt-3">
          <div className="flex flex-col w-full md:w-6/12">
            {stockMarkets && stockMarkets.length ? (
              <div className="flex flex-col pb-4">
                <Caption className="mb-2">Stock Market</Caption>
                <select
                  id="stock_market_id"
                  onChange={handleChange}
                  className="w-full text-black p-2 md:p-4 border rounded-md text-sm md:text-base"
                >
                  {stockMarkets.map((market: any) => (
                    <option
                      selected={d.stock_market_id === market.id ? true : false}
                      key={market.id}
                      value={market.id}
                    >
                      {market.market_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <TextFieldWithLabel
              id="email"
              label="Email"
              value={d.email}
              onChange={handleChange}
            />

            <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Subscription Start Date</Caption>
              <input
                type="date"
                id="sub_start_date"
                value={
                  d.sub_start_date ? d.sub_start_date.split("T")[0] : new Date()
                }
                onChange={handleChange}
                disabled={emailOnly}
                className="w-full p-2 border rounded-md text-sm md:text-base"
              />
            </div>

            <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Roles</Caption>
              {roles && roles.length ? (
                <select
                  id="role_id"
                  onChange={handleChange}
                  className="w-full text-black border p-2 rounded-md text-sm md:text-base"
                  disabled={emailOnly}
                >
                  {roles.map((role: any) => (
                    <option
                      selected={d.role === role.name ? true : false}
                      key={role.id}
                      value={role.id}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>

            <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Generate new password</Caption>
              <div className="flex items-center">
                <input
                  id="generate_password"
                  type="checkbox"
                  checked={d.generate_password || false}
                  onChange={handleChange}
                  disabled={emailOnly}
                  className="w-4 h-4"
                />
                <label htmlFor="generate_password" className="ml-2 text-sm">
                  Generate new password
                </label>
              </div>
            </div>
             <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Status</Caption>
             <div className="flex items-center">
              <input
                id="status"
                type="checkbox"
                checked={d.status === 1 || d.status === true}
                onChange={handleChange}
                disabled={emailOnly}
                className="w-4 h-4"
              />
                <label htmlFor="status" className="ml-2 text-sm">
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col w-full md:w-6/12">
            <div
              className={`${emailOnly ? "opacity-50 pointer-events-none" : ""}`}
            >
              <TextFieldWithLabel
                id="full_name"
                label="Full Name"
                value={d.full_name}
                onChange={handleChange}
              />
            </div>

            <div
              className={`${emailOnly ? "opacity-50 pointer-events-none" : ""}`}
            >
              <TextFieldWithLabel
                id="mobile_number"
                label="Mobile"
                value={d.mobile_number}
                onChange={handleChange}
              />
            </div>

            <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Subscription End Date</Caption>
              <input
                type="date"
                id="sub_end_date"
                value={
                  d.sub_end_date
                    ? d.sub_end_date.split("T")[0]
                    : add(new Date(), { days: 7 })
                }
                onChange={handleChange}
                disabled={emailOnly}
                className="w-full p-2 border rounded-md text-sm md:text-base"
              />
            </div>

            <div
              className={`flex w-full flex-col pb-4 ${
                emailOnly ? "opacity-50" : ""
              }`}
            >
              <Caption className="mb-2">Subscription Plans</Caption>
              {subscriptions && subscriptions.length ? (
                <select
                  id="sub_id"
                  onChange={handleChange}
                  className="w-full text-black border p-2 rounded-md text-sm md:text-base"
                  disabled={emailOnly}
                >
                  {subscriptions.map((sub: any) => (
                    <option
                      selected={d.sub_id === sub.id ? true : false}
                      key={sub.id}
                      value={sub.id}
                    >
                      {sub.plan}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default AddNewModal;
